import { Text } from "@mantine/core";

import ChatControlsBar from "@/components/chat/ChatControls/ChatControlsBar";
import { ChatPanelTimelineView } from "@/components/chat/ChatPanelTimelineView";
import { ChatQueue } from "@/components/chat/ChatQueue";
import { ExpertHubQueueButton } from "@/components/chat/ChatQueue/ExpertHubQueueButton";
import { useChatWorkspaceData } from "@/components/chat/hooks/useChatWorkspaceData";
import { useLogger } from "@/sdk";
import { ChatRightTopSidecar } from "@/sidecar/ChatRightTopSidecar";

import { useWorkspaceHomeState } from "./redesigned-layout/hooks/useWorkspaceHomeState";
import { WorkspaceMainSlot, WorkspaceRightSlot } from "./redesigned-layout/WorkspaceHomeSlots";
import { WorkspaceLayoutV2 } from "./redesigned-layout/WorkspaceLayoutV2";

/**
 * Redesign v2 chat workspace content (gated by `isChatWorkspaceRedesignV2Enabled`). This is the v1 chat
 * redesign plus the Expert Hub. Chat/session data is shared with the legacy
 * `ChatManager` via {@link useChatWorkspaceData} — change shared data needs there, not here, so the two
 * don't drift. It reuses every shared body component and adds the Expert Hub, leaving v1 unchanged.
 */
export function ChatWorkspaceContentRedesignedV2() {
    const logger = useLogger({ module: "ChatWorkspaceContentRedesignedV2", supportTeam: "messaging" });
    const {
        chatTasks,
        activeChatId,
        activeChatTask,
        onChatSelected,
        clearActiveChatId,
        isSelectedChat,
        canShowChatDetails,
        shouldShowQueue,
        isSalesEnabled,
        isChatExpertAssistEnabled,
        areGuidesAvailable,
        guide,
        guideToolboxCommand,
        isGuideOpen,
    } = useChatWorkspaceData({ logger });

    // Off-call = no active chat with details. The Expert Hub is the persistent home for that state.
    const { showExpertHub, rightTopOpen, rightTopSize, rightTopMin, rightTopMax } = useWorkspaceHomeState({
        isOffCall: !canShowChatDetails,
        baseRightTopOpen: canShowChatDetails,
    });

    return (
        <WorkspaceLayoutV2
            leftSidebar={
                <ChatQueue
                    chats={chatTasks}
                    isSelectedChat={isSelectedChat}
                    onChatSelected={onChatSelected}
                    header={
                        <>
                            {/* Return to the Expert Hub without closing a chat (deselects the active chat). */}
                            <ExpertHubQueueButton active={showExpertHub} onClick={clearActiveChatId} />
                            <Text size="xs" c="dimmed" fw="var(--mantine-font-weight-medium)" px="md" py="xs">
                                Chats
                            </Text>
                        </>
                    }
                />
            }
            leftSidebarOpen={shouldShowQueue}
            // Per product: the chat queue stays visible (experts juggle concurrent chats), unlike
            // voice's sidebar which hides on a call.
            leftSidebarHidden={false}
            leftTop={canShowChatDetails && activeChatTask ? <ChatControlsBar chatTask={activeChatTask} /> : undefined}
            leftTopOpen={canShowChatDetails}
            leftTopHeight="70px"
            leftBottom={
                <WorkspaceMainSlot
                    showExpertHub={showExpertHub}
                    onCallContent={<ChatPanelTimelineView activeChatTask={activeChatTask} />}
                />
            }
            rightTop={
                <WorkspaceRightSlot
                    showExpertHub={showExpertHub}
                    onCallContent={
                        canShowChatDetails ? (
                            <ChatRightTopSidecar
                                // Remount per chat (do not remove): the wrapper reads per-chat tab restore
                                // and the animateOnEnter seed once at mount, so they only re-evaluate when
                                // this key changes. Fresh-mounting unit tests won't catch a regression here.
                                key={activeChatId}
                                activeChatId={activeChatId}
                                customerSession={canShowChatDetails}
                                showAssistTab={isChatExpertAssistEnabled}
                                showOffersTab={isSalesEnabled}
                                // open() (not the raw openGuide) so the tab keeps its error-toast fallback
                                // when no guide is available to launch.
                                openGuide={guideToolboxCommand.open}
                                guide={guide}
                                areGuidesAvailable={areGuidesAvailable}
                                isGuideOpen={isGuideOpen}
                            />
                        ) : null
                    }
                />
            }
            rightTopOpen={rightTopOpen}
            rightTopSize={rightTopSize}
            rightTopMin={rightTopMin}
            rightTopMax={rightTopMax}
        />
    );
}
